/**
 * AvailableViews.jsx
 *
 * Renders a list of available project view types that can be added.
 * Each item has an "add" button to trigger the creation of a new view.
 */

import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import List from "@mui/material/List";
import { getProjectViewDefinitions } from "../../ProjectViews/projectViewUtils";
import ViewItem from "./ViewItem";

/**
 * Displays a list of available project view types with "add" actions.
 *
 * @component
 * @param {Object} props
 * @param {(viewType: number) => void} props.onAdd - Callback fired with the view type when an item is added.
 */
const AvailableViews = ({ onAdd }) => {
  const intl = useIntl();

  // Translate and sort available view types once per render cycle
  const availableViews = useMemo(() => {
    const translated = getProjectViewDefinitions().map((n) => ({
      type: n.value,
      name: intl.formatMessage({ id: n.translationKey }),
    }));

    return translated.sort((a, b) => a.name.localeCompare(b.name));
  }, [intl]);

  // Memoize the wrapper to prevent recreating callbacks for each ViewItem
  const handleAdd = useCallback((type) => {
    onAdd(type);
  }, [onAdd]);

  return (
    <List>
      {availableViews.map((view, index) => (
        <ViewItem
          key={index} // Index is fine since the list is static and order won't change
          name={view.name}
          onAdd={() => handleAdd(view.type)}
        />
      ))}
    </List>
  );
};

AvailableViews.propTypes = {
  onAdd: PropTypes.func.isRequired,
};

AvailableViews.displayName = "AvailableViews";

export default AvailableViews;
